/* Browserseite der Team-Mitteilungen.
 *
 * Hier steht bewusst alles, was mit Erlaubnis und Abo zu tun hat, damit die
 * Oberflaeche davon nichts wissen muss. Die Erlaubnis wird ausschliesslich aus
 * einer bewussten Nutzergeste angefragt: fragt eine Seite ungefragt beim Laden,
 * sperrt Safari die Domain dauerhaft — das liesse sich in der App nicht mehr
 * heilen.
 *
 * Konzept: docs/system/KONZEPT-TEAM-PUSH.md
 */

export type PushLage =
  | "nicht_unterstuetzt"     // dieser Browser kann kein Web Push
  | "home_bildschirm_noetig" // iPhone im Safari-Tab statt als App
  | "nicht_gefragt"          // Erlaubnis steht noch aus
  | "verweigert"             // Erlaubnis wurde abgelehnt
  | "aus"                    // erlaubt, aber kein Abo auf diesem Gerät
  | "an";

export function alsAppInstalliert(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

export function istApple(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function grundsaetzlichMoeglich(): boolean {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

/** Der VAPID-Schlüssel kommt als base64url und muss als Puffer übergeben werden.
 *  Rückgabe ist bewusst der ArrayBuffer: eine getypte Sicht darauf akzeptiert
 *  die Typdefinition von `applicationServerKey` nicht. */
function schluesselAlsPuffer(base64url: string): ArrayBuffer {
  const auffuellen = "=".repeat((4 - (base64url.length % 4)) % 4);
  const roh = atob((base64url + auffuellen).replace(/-/g, "+").replace(/_/g, "/"));
  const puffer = new ArrayBuffer(roh.length);
  const bytes = new Uint8Array(puffer);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return puffer;
}

async function registrierung(): Promise<ServiceWorkerRegistration | null> {
  if (!grundsaetzlichMoeglich()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function aktuellesAbo(): Promise<PushSubscription | null> {
  const reg = await registrierung();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function lage(): Promise<PushLage> {
  if (!grundsaetzlichMoeglich()) {
    // Auf dem iPhone fehlt PushManager, solange die Seite nur ein Safari-Tab ist.
    return istApple() && !alsAppInstalliert() ? "home_bildschirm_noetig" : "nicht_unterstuetzt";
  }
  if (istApple() && !alsAppInstalliert()) return "home_bildschirm_noetig";
  if (Notification.permission === "denied") return "verweigert";
  const abo = await aktuellesAbo();
  if (abo) return "an";
  return Notification.permission === "granted" ? "aus" : "nicht_gefragt";
}

export function geraetename(): string {
  if (typeof navigator === "undefined") return "Gerät";
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android-Telefon";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows-Rechner";
  return "Gerät";
}

/** Muss aus einem Klick heraus aufgerufen werden. Liefert das Abo als JSON. */
export async function anmelden(publicKey: string): Promise<Record<string, unknown>> {
  const reg = await registrierung();
  if (!reg) throw new Error("Dieser Browser kann keine Mitteilungen empfangen.");
  if (!publicKey) throw new Error("Auf dem Server sind noch keine Schlüssel hinterlegt.");

  const erlaubnis = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (erlaubnis !== "granted") throw new Error("Ohne deine Erlaubnis können wir dir nicht Bescheid geben.");

  const vorhanden = await reg.pushManager.getSubscription();
  const abo = vorhanden ?? await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: schluesselAlsPuffer(publicKey),
  });
  return { ...abo.toJSON(), geraet: geraetename(), user_agent: navigator.userAgent.slice(0, 400) };
}

/** Meldet dieses Gerät ab und gibt den Endpunkt zurück, damit der Server ihn löscht. */
export async function abmelden(): Promise<string> {
  const abo = await aktuellesAbo();
  if (!abo) return "";
  const endpunkt = abo.endpoint;
  try {
    await abo.unsubscribe();
  } catch {
    /* Der Server legt es ohnehin still, sobald der Endpunkt tot ist. */
  }
  return endpunkt;
}

/** Der Satz, der erklärt, woran es hakt — in der Sprache der App. */
export function hinweisZurLage(l: PushLage): string {
  switch (l) {
    case "nicht_unterstuetzt":
      return "Dieser Browser kann keine Mitteilungen.";
    case "home_bildschirm_noetig":
      return "Leg die App über Teilen → Zum Home-Bildschirm. Danach können wir dir Bescheid geben.";
    case "verweigert":
      return "Du hast Mitteilungen abgelehnt. In den Einstellungen deines Telefons kannst du sie wieder erlauben.";
    case "nicht_gefragt":
      return "Wir sagen dir Bescheid, wenn ein Einsatz dazukommt, sich verschiebt oder ausfällt.";
    case "aus":
      return "Auf diesem Gerät sind Mitteilungen gerade aus.";
    default:
      return "Wir melden uns bei neuen, verschobenen und abgesagten Einsätzen.";
  }
}
